import React, { useState, useRef } from "react";
import SEO from "../../../component/common/SEO";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { useLoader } from "../../../base/context/LoaderProvider";
import { NetworkConfiguration } from "../../../network/networkConfriguation";
import { callAPI } from "../../../network/networkConnection";
import { FaWallet, FaCloudUploadAlt, FaEye, FaTimes } from "react-icons/fa";
import "./style.css";

export default function RechargeRequestPage() {
  const [requestType, setRequestType] = useState("recharge"); // "recharge" | "deduct"
  const [form, setForm] = useState({
    senderName: "",
    userName: "",
    email: "",
    uniqueId: "",
    transactionId: "",
    transactionDate: "",
    amount: "",
    message: "",
  });
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [paymentProofUrl, setPaymentProofUrl] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const message = useMessage();
  const loader = useLoader();

  const [cinFetching, setCinFetching] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const fetchCinByEmail = async (email) => {
    const trimmed = email?.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return;
    setCinFetching(true);
    try {
      const res = await callAPI(
        process.env.REACT_APP_APIUrl + NetworkConfiguration.RECHARGE_CIN_BY_EMAIL + `?email=${encodeURIComponent(trimmed)}`,
        "GET",
        { "Content-Type": "application/json" }
      );
      if (res?.status?.code === 200 && res?.data?.uniqueId) {
        setForm((prev) => ({ ...prev, uniqueId: res.data.uniqueId }));
      }
    } catch (_) {}
    setCinFetching(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
      if (!validTypes.includes(file.type)) {
        setError("Please upload an image (JPEG, PNG or WebP)");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError("Image size must be less than 5MB");
        return;
      }
      setPaymentProofFile(file);
      setPaymentProofUrl("");
      setError("");
    }
  };

  const removeFile = () => {
    setPaymentProofFile(null);
    setPaymentProofUrl("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const uploadImage = async () => {
    if (!paymentProofFile) return null;
    const formData = new FormData();
    formData.append("image", paymentProofFile);
    const res = await fetch(
      process.env.REACT_APP_APIUrl + NetworkConfiguration.UPLOAD_RECHARGE_PROOF_PUBLIC,
      { method: "POST", body: formData }
    );
    const data = await res.json();
    if (data?.success && data?.url) return data.url;
    throw new Error(data?.message || "Upload failed");
  };

  const validate = () => {
    setError("");
    if (!form.senderName?.trim()) {
      setError("Your name is required");
      return false;
    }
    if (!form.userName?.trim()) {
      setError("Seller name is required");
      return false;
    }
    if (!form.email?.trim()) {
      setError("Registered email id is required");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError("Please enter a valid email");
      return false;
    }
    if (!form.uniqueId?.trim()) {
      setError("Unique ID (CIN) is required");
      return false;
    }
    if (requestType === "recharge") {
      if (!form.transactionId?.trim()) {
        setError("Transaction ID is required");
        return false;
      }
      if (!form.transactionDate) {
        setError("Transaction Date is required");
        return false;
      }
    }
    if (requestType === "recharge" && !paymentProofFile && !paymentProofUrl) {
      setError("Payment Proof is required for recharge (add money) requests");
      return false;
    }
    const amountNum = parseFloat(form.amount);
    if (!form.amount || isNaN(amountNum) || amountNum <= 0) {
      setError("Amount must be greater than 0");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    loader.showLoading(true);

    try {
      let proofUrl = paymentProofUrl;
      if (requestType === "recharge" && paymentProofFile) {
        proofUrl = await uploadImage();
      }

      const payload = {
        requestType,
        senderName: form.senderName.trim() || undefined,
        userName: form.userName.trim(),
        email: form.email.trim(),
        uniqueId: form.uniqueId.trim(),
        ...(requestType === "recharge"
          ? { transactionId: form.transactionId.trim(), transactionDate: new Date(form.transactionDate).toISOString() }
          : {}),
        ...(requestType === "recharge" && { paymentProofUrl: proofUrl }),
        amount: parseFloat(form.amount),
        message: form.message?.trim() || undefined,
      };

      const res = await callAPI(
        process.env.REACT_APP_APIUrl + NetworkConfiguration.RECHARGE_REQUEST_PUBLIC,
        "POST",
        { "Content-Type": "application/json" },
        JSON.stringify(payload)
      );

      loader.showLoading(false);
      setIsSubmitting(false);

      if (res?.status?.code === 200) {
        message.showToast(res.status.message || "Request submitted successfully", ToastTypes.SUCCESS);
        setForm({ senderName: "", userName: "", email: "", uniqueId: "", transactionId: "", transactionDate: "", amount: "", message: "" });
        removeFile();
        setRequestType("recharge");
      } else {
        message.showToast(res?.error?.message || "Failed to submit request", ToastTypes.ERROR);
      }
    } catch (err) {
      loader.showLoading(false);
      setIsSubmitting(false);
      message.showToast(err?.message || "Failed to submit request", ToastTypes.ERROR);
    }
  };

  return (
    <>
      <SEO title="IM Cash Recharge Request" />
      <div className="rc-page">
        <div className="rc-card">
          <div className="rc-header">
            <FaWallet className="rc-header-icon" />
            <h1 className="rc-title">IM Cash Recharge & Deduct</h1>
            <p className="rc-subtitle">
              {requestType === "recharge"
                ? "Submit a recharge request to add money to an IM Cash account. Admin will review and approve. Payment will be credited to the seller account matching Email and Unique ID."
                : "Submit a deduction request to withdraw money from your IM Cash account. Admin will review and approve; amount will be deducted from the seller account matching Email and Unique ID."}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="rc-form">
            <div className="rc-section-title">Request type</div>
            <div className="rc-type-toggle">
              <button
                type="button"
                className={`rc-type-btn ${requestType === "recharge" ? "rc-type-btn--active" : ""}`}
                onClick={() => setRequestType("recharge")}
                disabled={isSubmitting}
              >
                Recharge (add money)
              </button>
              <button
                type="button"
                className={`rc-type-btn ${requestType === "deduct" ? "rc-type-btn--active" : ""}`}
                onClick={() => setRequestType("deduct")}
                disabled={isSubmitting}
              >
                Deduct (withdraw request)
              </button>
            </div>

            <div className="rc-section-title">Request & User Details</div>
            <div className="rc-form-row rc-form-row--4">
              <div className="rc-field">
                <label>Your name *</label>
                <input
                  type="text"
                  name="senderName"
                  value={form.senderName}
                  onChange={handleChange}
                  placeholder="Your name"
                  disabled={isSubmitting}
                />
              </div>
              <div className="rc-field">
                <label>Seller name *</label>
                <input
                  type="text"
                  name="userName"
                  value={form.userName}
                  onChange={handleChange}
                  placeholder="Seller name"
                  disabled={isSubmitting}
                />
              </div>
              <div className="rc-field">
                <label>Registered email id *</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  onBlur={(e) => fetchCinByEmail(e.target.value)}
                  placeholder="Registered email"
                  disabled={isSubmitting}
                />
              </div>
              <div className="rc-field">
                <label>Unique ID (CIN) *</label>
                <input
                  type="text"
                  name="uniqueId"
                  value={form.uniqueId}
                  onChange={handleChange}
                  placeholder={cinFetching ? "Fetching…" : "Auto-filled from email"}
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <span className="rc-hint rc-hint--block">CIN is auto-fetched from registered email. Email + CIN must match the seller account.</span>

            <div className="rc-section-title">Transaction Details</div>
            <div className="rc-form-row rc-form-row--4">
              {requestType === "recharge" && (
                <>
                  <div className="rc-field">
                    <label>Transaction ID *</label>
                    <input
                      type="text"
                      name="transactionId"
                      value={form.transactionId}
                      onChange={handleChange}
                      placeholder="e.g. TXN123456789"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="rc-field">
                    <label>Transaction Date *</label>
                    <input
                      type="date"
                      name="transactionDate"
                      value={form.transactionDate}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </div>
                </>
              )}
              <div className="rc-field">
                <label>{requestType === "deduct" ? "Amount to deduct (₹) *" : "Amount (₹) *"}</label>
                <input
                  type="number"
                  name="amount"
                  value={form.amount}
                  onChange={handleChange}
                  placeholder="Enter amount"
                  min="1"
                  step="1"
                  disabled={isSubmitting}
                />
                <span className="rc-hint">Min: ₹1</span>
              </div>
              {requestType === "recharge" && (
                <div className="rc-field">
                  <label>Payment Proof *</label>
                  {paymentProofFile ? (
                    <div className="rc-file-row rc-file-row--compact">
                      <span className="rc-file-name" title={paymentProofFile.name}>{paymentProofFile.name}</span>
                      <div className="rc-file-actions">
                        <button
                          type="button"
                          className="rc-view-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            if (previewUrl) URL.revokeObjectURL(previewUrl);
                            setPreviewUrl(URL.createObjectURL(paymentProofFile));
                          }}
                        >
                          <FaEye /> View
                        </button>
                        <button type="button" className="rc-remove-btn" onClick={removeFile}>Remove</button>
                      </div>
                    </div>
                  ) : (
                    <div className={`rc-file-zone rc-file-zone--compact ${error ? "error" : ""}`} onClick={() => fileInputRef.current?.click()}>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                      />
                      <FaCloudUploadAlt className="rc-file-icon" />
                      <span>Upload (JPEG/PNG/WebP, max 5MB)</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="rc-field rc-field--full">
              <label>Message / Reason (optional)</label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder={requestType === "deduct" ? "e.g. reason for deduction or note for admin" : "e.g. why you need this recharge or note for admin"}
                disabled={isSubmitting}
                rows={2}
                className="rc-input rc-textarea"
              />
            </div>

            {error && (
              <div className="rc-error">
                {error}
              </div>
            )}

            <button type="submit" className={`rc-submit ${isSubmitting ? "rc-submit--loading" : ""}`} disabled={isSubmitting}>
              {isSubmitting && <span className="rc-spinner" aria-hidden />}
              {isSubmitting ? "Submitting…" : "Submit Request"}
            </button>
          </form>
        </div>
      </div>

      {previewUrl && (
        <div className="rc-preview-overlay" onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
          <div className="rc-preview-box" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="rc-preview-close" onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
              <FaTimes />
            </button>
            <img src={previewUrl} alt="Payment proof" />
          </div>
        </div>
      )}
    </>
  );
}
